import React from 'react';
import { Box, Grid, Paper, Typography, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CommentIcon from '@mui/icons-material/Comment';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PeopleIcon from '@mui/icons-material/People';
import { getCachedResult, CACHE_TYPES } from '../services/cache';

function Dashboard() {
  const [summaryData, setSummaryData] = React.useState({
    trending: {
      title: 'Top Hashtags',
      icon: TrendingUpIcon,
      data: [],
      summary: 'No hashtags analyzed'
    },
    comments: {
      title: 'Comments',
      icon: CommentIcon,
      data: 0,
      summary: 'No comments analyzed'
    },
    likeRate: {
      title: 'Like Rate',
      icon: FavoriteIcon,
      data: '0%',
      summary: 'No like rate data'
    },
    conversion: {
      title: 'Conversion',
      icon: PeopleIcon,
      data: '0%',
      summary: 'No conversion data'
    },
  });

  // Function to update dashboard data
  const updateDashboardData = React.useCallback(() => {
    const newData = {
      trending: {
        title: 'Top Hashtags',
        icon: TrendingUpIcon,
        data: [],
        summary: 'No hashtags analyzed'
      },
      comments: {
        title: 'Comments',
        icon: CommentIcon,
        data: 0,
        summary: 'No comments analyzed'
      },
      likeRate: {
        title: 'Like Rate',
        icon: FavoriteIcon,
        data: '0%',
        summary: 'No like rate data'
      },
      conversion: {
        title: 'Conversion',
        icon: PeopleIcon,
        data: '0%',
        summary: 'No conversion data'
      },
    };

    let hashtagsFound = new Set();
    let totalComments = 0;
    let totalLikes = 0;
    let totalViews = 0;

    // Get all hashtag analysis data from localStorage
    const hashtagKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_TYPES.HASHTAG)) {
        try {
          const hashtagData = JSON.parse(localStorage.getItem(key));
          if (hashtagData?.data) {
            hashtagsFound.add(hashtagData.data.hashtag);
            // eslint-disable-next-line no-loop-func
            hashtagData.data.videos?.forEach(video => {
              totalLikes += video.stats?.diggCount || 0;
              totalViews += video.stats?.playCount || 0;
              totalComments += video.stats?.commentCount || 0;
            });
            hashtagKeys.push(key);
          }
        } catch (error) {
          console.error('Error parsing hashtag data:', error);
        }
      }
    }


    // Get comment analysis data
    const commentData = getCachedResult(CACHE_TYPES.COMMENT);
    if (commentData?.data) {
      let analyzedComments = 0;
      if (commentData.data.topComments) {
        analyzedComments += commentData.data.topComments.length;
      }
      if (commentData.data.positiveScore !== undefined) {
        analyzedComments++;
      }
      if (commentData.data.negativeScore !== undefined) {
        analyzedComments++;
      }
      totalComments += analyzedComments;
    }

    // Get like rate analysis data
    const likeRateData = getCachedResult(CACHE_TYPES.LIKE_RATE);
    if (likeRateData?.data?.videos) {
      likeRateData.data.videos.forEach(video => {
        totalLikes += video.stats?.diggCount || 0;
        totalViews += video.stats?.playCount || 0;
      });
    }

    // Get conversion rate analysis data
    const conversionData = getCachedResult(CACHE_TYPES.CONVERSION);
    if (conversionData?.data?.videos) {
      conversionData.data.videos.forEach(video => {
        totalLikes += video.stats?.diggCount || 0;
        totalViews += video.stats?.playCount || 0;
        totalComments += video.stats?.commentCount || 0;
      });
    }

    // Update summary data
    if (hashtagsFound.size > 0) {
      newData.trending.data = Array.from(hashtagsFound);
      newData.trending.summary = `${hashtagsFound.size} trending hashtags`;
    }

    if (totalComments > 0) {
      newData.comments.data = totalComments;
      newData.comments.summary = 'Total comments analyzed';
    }

    if (totalViews > 0) {
      const likeRate = ((totalLikes / totalViews) * 100).toFixed(1);
      newData.likeRate.data = `${likeRate}%`;
      newData.likeRate.summary = 'Average like rate';

      const conversionRate = ((totalComments / totalViews) * 100).toFixed(1);
      newData.conversion.data = `${conversionRate}%`;
      newData.conversion.summary = 'Average conversion rate';
    }

    setSummaryData(newData);
  }, []);

  // Initialize dashboard with cached data
  React.useEffect(() => {
    console.log('Dashboard component mounted');
    // Load cached data immediately
    updateDashboardData();

    // Listen for cache updates
    const handleCacheUpdate = (event) => {
      console.log('Cache updated, refreshing dashboard:', event.detail);
      updateDashboardData();
    };

    window.addEventListener('cacheUpdated', handleCacheUpdate);

    // Also refresh data periodically to ensure it stays in sync
    const refreshInterval = setInterval(updateDashboardData, 30000); // Refresh every 30 seconds

    return () => {
      window.removeEventListener('cacheUpdated', handleCacheUpdate);
      clearInterval(refreshInterval);
    };
  }, [updateDashboardData]);

  // Helper function to get descriptive tooltips for each metric
  const getMetricDescription = (key) => {
    const descriptions = {
      trending: "Discover which hashtags are currently trending and gaining traction in your niche. This helps you optimize your content's reach.",
      comments: "Track the engagement through comments on your content. Higher comment counts often indicate better audience connection.",
      likeRate: "Your content's like-to-view ratio. This metric helps gauge how well your content resonates with viewers.",
      conversion: "The percentage of viewers who engage with your content through comments. A higher rate indicates strong audience engagement.",
      timing: "The optimal time to post based on when your audience is most active and engaged. This helps maximize your content's initial reach."
    };
    return descriptions[key] || "Analyze your content performance with Creator IQ";
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        mb: 4,
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
        borderRadius: 2,
        p: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ 
            width: { xs: 40, sm: 56 }, // Responsive width
            height: { xs: 40, sm: 56 }, // Responsive height
            background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            flexShrink: 0 // Prevent the logo container from shrinking
          }}>
            <Box sx={{ 
              fontSize: { xs: 18, sm: 24 }, // Responsive font size
              fontWeight: 700,
              color: 'white',
              lineHeight: 1,
              display: 'flex'
            }}>
                  <span style={{ display: 'inline-block' }}>I</span>
                  <span style={{ display: 'inline-block', marginLeft: '2px' }}>Q</span>
            </Box>
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Creator IQ
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your AI-powered content analytics assistant
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh insights">
          <IconButton 
            onClick={updateDashboardData}
            sx={{
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
              }
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
        {Object.entries(summaryData).map(([key, { title, icon: Icon, data, summary }]) => (
          <Grid item xs={12} sm={6} lg={4} xl={3} key={key}>
            <Paper
              sx={{
              p: { xs: 2, sm: 3, md: 4 },
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
                position: 'relative',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)'
                }
              }}
            >
              <Tooltip title={getMetricDescription(key)} placement="top">
                <IconButton 
                  size="small" 
                  sx={{ 
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    opacity: 0.6,
                    '&:hover': { opacity: 1 }
                  }}
                >
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
                <Box sx={{
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  borderRadius: '50%',
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icon sx={{ 
                    color: 'white',
                    fontSize: 24
                  }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {title}
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                mb: 1, 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {Array.isArray(data) ? data.join(', ') : data}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', fontSize: '0.875rem' }}>
                <Box sx={{ 
                  display: 'flex',
                  fontSize: 16,
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginRight: 1
                }}>
                  <span>I</span>
                  <span style={{ marginLeft: '2px' }}>Q</span>
                </Box>
                {summary}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default Dashboard;
